import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { formatPercent } from "@/features/matches";
import { cn } from "@/shared/lib";

import type { FriendMate, FriendsBoard } from "../../model/FriendsBoard";
import type { FriendsPeriod } from "../../model/FRIENDS_PERIODS";
import { FriendsSection } from "./FriendsSection";
import { SteamAvatar } from "./SteamAvatar";

const MORE = msg({
  message:
    "{points} points more than without friends, {count, plural, one {# match} other {# matches}} together",
});
const LESS = msg({ message: "{points} points less than without friends" });
const NOT_WORSE = msg({ message: "still not worse than without friends" });
const SOLO = msg({ message: "your win rate without friends — {rate}" });
const MIN_NOTE = msg({
  message:
    "Shows friends with at least {count, plural, one {# match} other {# matches}} on the same team.",
});

type MatesSectionProps = {
  board: FriendsBoard;
  period: FriendsPeriod;
  minMatches: number;
  refreshing?: boolean;
};

const rate = (mate: FriendMate) => mate.wins / mate.matches;

// «С кем ты побеждаешь»: доля побед в одной команде с каждым другом против твоей доли
// побед без них. Работает и с друзьями, которых нет в Relock, и при закрытом списке друзей
// (тогда — только напарники по группе).
export const MatesSection = ({
  board,
  period,
  minMatches,
  refreshing = false,
}: MatesSectionProps) => {
  const { i18n, t } = useLingui();
  const mates = board.mates;
  const solo = board.solo.matches > 0 ? board.solo.wins / board.solo.matches : null;
  const points = (share: number) => Math.round(share * 100);
  const periodText: Record<FriendsPeriod, string> = {
    week: t`over the week`,
    month: t`over the month`,
    all: t`over all time`,
  };
  const when = periodText[period];
  const description = board.list_private
    ? t`While your friend list is hidden, only party mates are shown — ${when}.`
    : t`Matches on the same team with Steam friends ${when}, including those not on Relock.`;

  const sorted = [...mates].sort((a, b) => rate(b) - rate(a));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];

  return (
    <FriendsSection
      title={<Trans>Who you win with</Trans>}
      description={description}
      refreshing={refreshing}
    >
      {mates.length === 0 ? (
        <p className="text-sm text-subtle">
          <Trans>No matches on the same team with friends in this period.</Trans>
        </p>
      ) : (
        <>
          {best !== undefined && worst !== undefined && best !== worst && solo !== null && (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2.5">
              <div className="flex flex-col gap-0.5 rounded-[10px] border border-good/40 bg-sidebar px-3.5 py-3">
                <span className="text-xs text-subtle">
                  <Trans>Best results with</Trans>
                </span>
                <span className="flex flex-wrap items-baseline gap-2.5">
                  <span className="text-[17px] font-semibold">
                    {best.display_name ?? t`Friend`}
                  </span>
                  <span className="font-mono font-semibold text-good">
                    {formatPercent(rate(best), i18n.locale)}
                  </span>
                </span>
                <span className="text-[13px] text-muted">
                  {rate(best) > solo
                    ? i18n._({
                        ...MORE,
                        values: { points: points(rate(best) - solo), count: best.matches },
                      })
                    : i18n._(NOT_WORSE)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 rounded-[10px] border border-bad/40 bg-sidebar px-3.5 py-3">
                <span className="text-xs text-subtle">
                  <Trans>Worst results with</Trans>
                </span>
                <span className="flex flex-wrap items-baseline gap-2.5">
                  <span className="text-[17px] font-semibold">
                    {worst.display_name ?? t`Friend`}
                  </span>
                  <span className="font-mono font-semibold text-bad">
                    {formatPercent(rate(worst), i18n.locale)}
                  </span>
                </span>
                <span className="text-[13px] text-muted">
                  {rate(worst) < solo
                    ? i18n._({ ...LESS, values: { points: points(solo - rate(worst)) } })
                    : i18n._(NOT_WORSE)}
                </span>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-separate border-spacing-y-1.5 text-left">
              <thead>
                <tr className="text-xs tracking-[0.06em] text-subtle uppercase">
                  <th scope="col" className="px-3 font-normal">
                    <Trans>Friend</Trans>
                  </th>
                  <th scope="col" className="w-24 font-normal">
                    <Trans>Together</Trans>
                  </th>
                  <th scope="col" className="font-normal">
                    <Trans>Wins together</Trans>
                  </th>
                  <th scope="col" className="w-16 text-right font-normal">
                    %
                  </th>
                  <th scope="col" className="w-20 px-3 text-right font-normal">
                    <Trans>Difference</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {mates.map((mate) => {
                  const share = rate(mate);
                  const delta = solo === null ? null : points(share - solo);
                  return (
                    <tr key={mate.account_id} className="bg-sidebar">
                      <td className="rounded-l-[10px] border-y border-l border-sidebar-active px-3 py-2">
                        <span className="flex min-w-0 items-center gap-2.5">
                          <SteamAvatar name={mate.display_name} url={mate.avatar_url} />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate font-semibold">
                              {mate.display_name ?? t`Steam player`}
                            </span>
                            <span
                              className={cn(
                                "text-xs",
                                mate.in_relock ? "text-accent" : "text-subtle",
                              )}
                            >
                              {mate.in_relock ? t`on Relock` : t`not on Relock`}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="border-y border-sidebar-active font-mono text-sm text-muted">
                        {mate.matches}
                      </td>
                      <td className="border-y border-sidebar-active pr-3">
                        <span
                          aria-hidden="true"
                          className="relative block h-2.5 rounded bg-sidebar-active"
                        >
                          <span
                            className={cn(
                              "absolute inset-y-0 left-0 rounded",
                              delta === null || delta >= 0 ? "bg-good" : "bg-bad",
                            )}
                            style={{ width: `${String(points(share))}%` }}
                          />
                          {solo !== null && (
                            <span
                              className="absolute -inset-y-1 w-0.5 bg-foreground"
                              style={{ left: `calc(${String(points(solo))}% - 1px)` }}
                            />
                          )}
                        </span>
                      </td>
                      <td className="border-y border-sidebar-active text-right font-mono font-semibold">
                        {formatPercent(share, i18n.locale)}
                      </td>
                      <td
                        className={cn(
                          "rounded-r-[10px] border-y border-r border-sidebar-active px-3 text-right font-mono font-semibold",
                          delta === null || delta === 0
                            ? "text-subtle"
                            : delta > 0
                              ? "text-good"
                              : "text-bad",
                        )}
                      >
                        {delta === null
                          ? "—"
                          : delta > 0
                            ? `+${String(delta)}`
                            : delta < 0
                              ? `−${String(-delta)}`
                              : "0"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-subtle">
            {solo !== null && (
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="inline-block h-3.5 w-0.5 bg-foreground" />
                {i18n._({ ...SOLO, values: { rate: formatPercent(solo, i18n.locale) } })}
              </span>
            )}
            <span>{i18n._({ ...MIN_NOTE, values: { count: minMatches } })}</span>
          </p>
        </>
      )}
    </FriendsSection>
  );
};
